import React from "react";
import {
  UserOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  LinkOutlined,
} from "@ant-design/icons";

interface SLAIndividualPerformanceCardsProps {
  userData: any;
  selectedTeam: string;
  opsAdminUsers?: any;
}

interface UserCardProps {
  user: any;
  isOpsAdmin?: boolean;
}

// Helper functions for styling
const getComplianceColor = (percentage: number) => {
  if (percentage >= 90) return "#10b981"; // Green
  if (percentage >= 80) return "#f59e0b"; // Orange
  if (percentage >= 70) return "#f97316"; // Orange-red
  return "#ef4444"; // Red
};

const getComplianceColorClass = (percentage: number) => {
  if (percentage >= 90) return "text-green-600";
  if (percentage >= 80) return "text-yellow-600";
  if (percentage >= 70) return "text-orange-600";
  return "text-red-600";
};

const getComplianceBadge = (percentage: number) => {
  if (percentage >= 90) return "Excelente";
  if (percentage >= 80) return "Bueno";
  if (percentage >= 70) return "Regular";
  return "Necesita Mejora";
};

const getComplianceBadgeColor = (percentage: number) => {
  if (percentage >= 90) return "bg-green-100 text-green-800";
  if (percentage >= 80) return "bg-yellow-100 text-yellow-800";
  if (percentage >= 70) return "bg-orange-100 text-orange-800";
  return "bg-red-100 text-red-800";
};

const ProgressBar = ({
  percentage,
  color,
}: {
  percentage: number;
  color: string;
}) => (
  <div className="w-full bg-gray-200 rounded-full h-2">
    <div
      className="h-2 rounded-full transition-all duration-300"
      style={{
        width: `${Math.min(percentage, 100)}%`,
        backgroundColor: color,
      }}
    />
  </div>
);

// Reusable User Card Component
const UserCard: React.FC<UserCardProps> = ({ user, isOpsAdmin = false }) => {
  const cardBgClass = isOpsAdmin ? "bg-purple-50" : "bg-gray-50";
  const cardBorderClass = isOpsAdmin
    ? "border-2 border-purple-200"
    : "border border-gray-200";
  const iconBgClass = isOpsAdmin ? "bg-purple-100" : "bg-blue-100";
  const iconColorClass = isOpsAdmin ? "text-purple-600" : "text-blue-600";
  const buttonColorClass = isOpsAdmin
    ? "text-purple-600 hover:text-purple-800"
    : "text-blue-600 hover:text-blue-800";
  const userDetailUrl = isOpsAdmin
    ? `/sla-metrics/users/${encodeURIComponent(user.user_id)}?level=direct`
    : `/sla-metrics/users/${encodeURIComponent(user.user_id)}`;

  return (
    <div
      className={`${cardBgClass} rounded-lg p-5 ${cardBorderClass} hover:shadow-md transition-shadow duration-200`}
    >
      {/* User Header */}
      <div className="flex items-center mb-4">
        <div className={`${iconBgClass} rounded-full p-2 mr-3`}>
          <UserOutlined className={`${iconColorClass} text-lg`} />
        </div>
        <div className="flex-1 min-w-0">
          <button
            className={`text-lg font-semibold ${buttonColorClass} cursor-pointer truncate transition-colors duration-200 flex items-center group`}
            onClick={() =>
              window.open(userDetailUrl, "_blank", "noopener,noreferrer")
            }
            title="Click para ver métricas detalladas"
          >
            {user.user_name}
            <LinkOutlined className="ml-2 text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
          </button>
          <div className="text-sm text-gray-500 truncate">
            {user.user_email}
          </div>
        </div>
      </div>

      {/* Overall Compliance */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">
            Cumplimiento General
          </span>
          <span
            className={`text-lg font-bold ${getComplianceColorClass(user.compliance_pct)}`}
          >
            {user.compliance_pct.toFixed(1)}%
          </span>
        </div>
        <ProgressBar
          percentage={user.compliance_pct}
          color={getComplianceColor(user.compliance_pct)}
        />
        <div className="mt-2">
          <span
            className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${getComplianceBadgeColor(user.compliance_pct)}`}
          >
            {getComplianceBadge(user.compliance_pct)}
          </span>
        </div>
      </div>

      {/* Detailed Metrics */}
      <div className="space-y-3">
        {/* Before 1:00 PM */}
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <ClockCircleOutlined className="text-blue-500 text-sm mr-2" />
            <span className="text-sm text-gray-600">Primer bloque</span>
          </div>
          <span className={`text-sm font-medium`}>
            {user.before_cutoff_pct.toFixed(1)}%
          </span>
        </div>

        {/* After 1:00 PM */}
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <ExclamationCircleOutlined className="text-orange-500 text-sm mr-2" />
            <span className="text-sm text-gray-600">Segundo bloque</span>
          </div>
          <span className={`text-sm font-medium`}>
            {user.after_cutoff_pct.toFixed(1)}%
          </span>
        </div>

        {/* Average business hours */}
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <ClockCircleOutlined className="text-green-500 text-sm mr-2" />
            <span className="text-sm text-gray-600">Horas promedio</span>
          </div>
          <span className={`text-sm font-medium`}>
            {user.avg_verification_time_business_hours.toFixed(1)}
          </span>
        </div>

        {/* Separator */}
        <hr className="border-gray-200" />

        {/* Summary Stats */}
        <div className="grid grid-cols-2 gap-2 text-center">
          <div>
            <div className="text-lg font-bold text-gray-900">
              {user.total_processed.toLocaleString()}
            </div>
            <div className="text-xs text-gray-500">Procesados</div>
          </div>
          <div>
            <div className="text-lg font-bold text-green-600">
              {user.compliant.toLocaleString()}
            </div>
            <div className="text-xs text-gray-500">Cumplidos</div>
          </div>
        </div>

        {/* Performance Indicator */}
        <div className="text-center text-xs text-gray-500 pt-2 border-t border-gray-200">
          {user.compliant}/{user.total_processed} requisitos
          <div className="mt-1">
            <CheckCircleOutlined
              className="mr-1"
              style={{ color: getComplianceColor(user.compliance_pct) }}
            />
            {((user.compliant / user.total_processed) * 100).toFixed(1)}%
            efectividad
          </div>
        </div>
      </div>
    </div>
  );
};

const SLAIndividualPerformanceCards: React.FC<
  SLAIndividualPerformanceCardsProps
> = ({ userData, selectedTeam, opsAdminUsers }) => {
  if (!userData || !userData.combined_compliance) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-medium mb-4 text-gray-700">
          Rendimiento Individual
        </h2>
        <div className="flex justify-center items-center h-32">
          <p className="text-gray-500">No hay datos de usuarios disponibles</p>
        </div>
      </div>
    );
  }

  const activeUsers = userData.combined_compliance.filter(
    (user: any) => user.total_processed > 0,
  );

  // Filter out ops-admin users from the regular user list to prevent duplicates
  const activeUsersIds = new Set(activeUsers.map((user: any) => user.user_id));
  const filteredOpsAdminUsers =
    opsAdminUsers?.filter((user: any) => !activeUsersIds.has(user.user_id)) ||
    [];

  // Check if we should show the empty state
  const hasOpsAdminUsers =
    selectedTeam === "all" && filteredOpsAdminUsers.length > 0;

  if (activeUsers.length === 0 && !hasOpsAdminUsers) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-medium mb-4 text-gray-700">
          Rendimiento Individual
          {selectedTeam !== "all" && (
            <span className="text-sm text-gray-500 ml-2 capitalize">
              - Equipo {selectedTeam}
            </span>
          )}
        </h2>
        <div className="flex justify-center items-center h-32">
          <p className="text-gray-500">No hay usuarios con datos procesados</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-lg font-medium mb-4 text-gray-700">
        Rendimiento Individual
        {selectedTeam !== "all" && (
          <span className="text-sm text-gray-500 ml-2 capitalize">
            - Equipo {selectedTeam}
          </span>
        )}
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {activeUsers.map((user: any) => (
          <UserCard key={user.user_id} user={user} isOpsAdmin={false} />
        ))}

        {/* Ops-Admin Users - Special tracked users */}
        {selectedTeam === "all" && filteredOpsAdminUsers.length > 0 && (
          <>
            <div className="col-span-full mt-6 mb-2 border-t border-gray-300 pt-4">
              <h3 className="text-md font-semibold text-purple-700 flex items-center">
                <UserOutlined className="mr-2" />
                Usuarios (Ops-Admin)
              </h3>
            </div>
            {filteredOpsAdminUsers.map((user: any) => (
              <UserCard key={user.user_id} user={user} isOpsAdmin={true} />
            ))}
          </>
        )}
      </div>

      {/* Summary Footer */}
      {activeUsers.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center text-sm">
            <div>
              <div className="font-semibold text-gray-900">
                {activeUsers.length}
              </div>
              <div className="text-gray-500">Usuarios Activos</div>
            </div>
            <div>
              <div className="font-semibold text-gray-900">
                {activeUsers
                  .reduce(
                    (sum: number, user: any) => sum + user.total_processed,
                    0,
                  )
                  .toLocaleString()}
              </div>
              <div className="text-gray-500">Total Procesados</div>
            </div>
            <div>
              <div className="font-semibold text-green-600">
                {activeUsers
                  .reduce((sum: number, user: any) => sum + user.compliant, 0)
                  .toLocaleString()}
              </div>
              <div className="text-gray-500">Total Cumplidos</div>
            </div>
            <div>
              <div className="font-semibold text-blue-600">
                {
                  activeUsers.filter((user: any) => user.compliance_pct >= 85)
                    .length
                }
              </div>
              <div className="text-gray-500">Cumplen Meta</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SLAIndividualPerformanceCards;
